import { Bar, FrontDesk, Housekeeping, Kiosk, Location, Room, Staff } from '@era/locations-sdk';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Category, ConcreteCategory } from '../router/category';
import { Brand, CategoryType, CategoryTypeToEntity } from '../shared/types';

/**
 * Mapped type that generates CategoryEntity types for each CategoryType
 */
export type CategoryEntityMap = { [T in CategoryType]: ConcreteCategory<T> };

/**
 * Union type of all CategoryEntity types
 */
export type CategoriesUnion = CategoryEntityMap[CategoryType];

/**
 * Union type of all Entity types
 */
export type EntitiesUnion = CategoryTypeToEntity[CategoryType];

/**
 * Type mapping from CategoryType to its corresponding CategoryEntity type
 */
export type CategoryEntityByType<T extends CategoryType> = CategoryEntityMap[T];

/**
 * Sort function for Room entities by displayName
 */
export function sortRoomsByDisplayName(a: Room, b: Room): number {
  return a.displayName.localeCompare(b.displayName, 'en', { numeric: true });
}

export function categoryEntityFrom(
  categoryType: 'Brand',
  entity: { id: string; displayName: string; uniqueId: string }
): CategoryEntityMap['Brand'];
export function categoryEntityFrom(categoryType: 'Location', entity: Location): CategoryEntityMap['Location'];
export function categoryEntityFrom(categoryType: 'Room', entity: Room): CategoryEntityMap['Room'];
export function categoryEntityFrom(categoryType: 'Bar', entity: Bar): CategoryEntityMap['Bar'];
export function categoryEntityFrom(categoryType: 'FrontDesk', entity: FrontDesk): CategoryEntityMap['FrontDesk'];
export function categoryEntityFrom(
  categoryType: 'Housekeeping',
  entity: Housekeeping
): CategoryEntityMap['Housekeeping'];
export function categoryEntityFrom(categoryType: 'Kiosk', entity: Kiosk): CategoryEntityMap['Kiosk'];
export function categoryEntityFrom(categoryType: 'Staff', entity: Staff): CategoryEntityMap['Staff'];
export function categoryEntityFrom(categoryType: 'None', entity: never): never;
export function categoryEntityFrom(categoryType: CategoryType, entity: EntitiesUnion): CategoriesUnion {
  const brand = new ConcreteCategory('Brand', 'default-brand', 'default-brand', 'Default Brand', undefined, undefined);

  switch (categoryType) {
    case 'Location': {
      const location = entity as Location;
      return new ConcreteCategory('Location', location.uniqueId, location.id, location.displayName, location, brand);
    }
    case 'Room': {
      const room = entity as Room;
      const location: Category = { categoryType: 'Location', categoryId: room.locationUniqueId, parent: brand };
      return new ConcreteCategory('Room', room.uniqueId, room.id, room.displayName, room, location);
    }
    case 'Bar': {
      const bar = entity as Bar;
      const location: Category = { categoryType: 'Location', categoryId: bar.locationUniqueId, parent: brand };
      return new ConcreteCategory('Bar', bar.id, bar.humanId, bar.displayName, bar, location);
    }
    case 'FrontDesk': {
      const frontDesk = entity as FrontDesk;
      const location: Category = { categoryType: 'Location', categoryId: frontDesk.locationUniqueId, parent: brand };
      return new ConcreteCategory(
        'FrontDesk',
        frontDesk.id,
        frontDesk.humanId,
        frontDesk.displayName,
        frontDesk,
        location
      );
    }
    case 'Housekeeping': {
      const housekeeping = entity as Housekeeping;
      const location: Category = {
        categoryType: 'Location',
        categoryId: housekeeping.locationUniqueId,
        parent: brand,
      };
      return new ConcreteCategory(
        'Housekeeping',
        housekeeping.id,
        housekeeping.humanId,
        housekeeping.displayName,
        housekeeping,
        location
      );
    }
    case 'Kiosk': {
      const kiosk = entity as Kiosk;
      const location: Category = { categoryType: 'Location', categoryId: kiosk.locationUniqueId, parent: brand };
      return new ConcreteCategory('Kiosk', kiosk.id, kiosk.humanId, kiosk.displayName, kiosk, location);
    }
    case 'Staff': {
      const staff = entity as Staff;
      const location: Category = { categoryType: 'Location', categoryId: staff.locationUniqueId, parent: brand };
      return new ConcreteCategory('Staff', staff.id, staff.humanId, staff.displayName, staff, location);
    }
    case 'Brand': {
      const brand = entity as Brand;
      return new ConcreteCategory('Brand', brand.id, brand.humanId, brand.displayName, brand, undefined);
    }
    default:
      throw new Error(`Unknown category type: ${categoryType}`);
  }
}

export type CategoryState = EntityState<CategoriesUnion>;

export function selectId(configuration: CategoriesUnion): string {
  return selectIdFromKeys(configuration.categoryType, configuration.categoryId);
}

export function selectIdFromKeys(categoryType: CategoryType, categoryId: string): string {
  return `${categoryType}-${categoryId}`;
}

export const adapter: EntityAdapter<CategoriesUnion> = createEntityAdapter<CategoriesUnion>({
  selectId,
});

export const initialState: CategoryState = adapter.getInitialState();
